"use client";

import { useState, useEffect, useRef } from "react";
import Map, {
  Marker,
  Popup,
  NavigationControl,
  ScaleControl,
} from "react-map-gl/maplibre";
import { Battery, Zap, MapPin, Navigation } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useClickOutside } from "@mantine/hooks";

type ChargingStation = {
  id: string;
  owner?: string;
  displayName?: string;
  name: string;
  address: string;
  latitude: number;
  longitude: number;
  distance?: number;
  maxCurrent?: number;
  chargingPoints?: {
    leistung: string;
    steckertyp: string;
  }[];
};

type ChargingStationMapProps = {
  stations: ChargingStation[];
  centerLat: number;
  centerLon: number;
  radius: number;
};

export default function ChargingStationMap({
  stations,
  centerLat,
  centerLon,
  radius,
}: ChargingStationMapProps) {
  const [selectedStation, setSelectedStation] =
    useState<ChargingStation | null>(null);
  const mapRef = useRef(null);
  const [viewport, setViewport] = useState({
    latitude: centerLat,
    longitude: centerLon,
    zoom: 11,
  });

  const popoverRef = useClickOutside(() => {
    setSelectedStation(null);
  });

  useEffect(() => {
    // Update viewport when center coordinates change
    setViewport((prev) => ({
      ...prev,
      latitude: centerLat,
      longitude: centerLon,
    }));
  }, [centerLat, centerLon]);

  const generateAppleMapsUrl = (lat: number, lon: number, name: string) => {
    return `https://maps.apple.com/?daddr=${lat},${lon}&dirflg=d&t=m&q=${encodeURIComponent(name)}`;
  };

  const getMarkerColor = (station: ChargingStation) => {
    // If the station has charging points with high power, return orange
    if (
      station.chargingPoints?.some((cp) => {
        const leistung = parseFloat(cp.leistung);
        return !isNaN(leistung) && leistung >= 50;
      })
    ) {
      return "text-orange-500";
    }

    // If the station has a high maxCurrent, return orange
    if (station.maxCurrent && station.maxCurrent >= 50) {
      return "text-orange-500";
    }

    // Default color
    return "text-blue-500";
  };

  return (
    <div className="h-full w-full">
      <Map
        ref={mapRef}
        // mapLib={import("maplibre-gl")}
        mapStyle="https://tiles.openfreemap.org/styles/bright"
        {...viewport}
        onMove={(evt) => setViewport(evt.viewState)}
        style={{ width: "100%", height: "100%" }}
      >
        <ScaleControl position="bottom-right" />
        <NavigationControl position="top-right" />

        {stations.map((station) => (
          <Marker
            key={station.id}
            latitude={station.latitude}
            longitude={station.longitude}
            anchor="bottom"
            onClick={(e) => {
              e.originalEvent.stopPropagation();
              setSelectedStation(station);
            }}
          >
            <div className="relative cursor-pointer">
              <MapPin className={`h-6 w-6 ${getMarkerColor(station)}`} />
              {station.maxCurrent && station.maxCurrent >= 50 && (
                <div className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-orange-500 text-white">
                  <Zap className="h-3 w-3" />
                </div>
              )}
            </div>
          </Marker>
        ))}

        {selectedStation && (
          <Popup
            latitude={selectedStation.latitude}
            longitude={selectedStation.longitude}
            anchor="bottom"
            onClose={() => setSelectedStation(null)}
            className="max-w-sm overflow-hidden rounded-lg p-2"
            closeButton={false}
            closeOnClick={true}
          >
            <div className="p-1">
              <h3 className="mb-1 text-sm font-bold">{selectedStation.name}</h3>
              <p className="mb-2 text-xs text-gray-600">
                {selectedStation.address}
              </p>

              <div className="mb-2 flex flex-wrap gap-1">
                {selectedStation.maxCurrent &&
                  selectedStation.maxCurrent >= 50 && (
                    <Badge
                      variant="secondary"
                      className="bg-orange-100 text-xs text-orange-800"
                    >
                      Schnellladen
                    </Badge>
                  )}
                {selectedStation.distance && (
                  <Badge variant="outline" className="text-xs">
                    {(selectedStation.distance / 1000).toFixed(1)} km
                  </Badge>
                )}
                {selectedStation.chargingPoints && (
                  <Badge variant="outline" className="text-xs">
                    {selectedStation.chargingPoints.length} Ladepunkte
                  </Badge>
                )}
              </div>

              {selectedStation.chargingPoints &&
                selectedStation.chargingPoints.length > 0 && (
                  <div className="mb-2">
                    <h4 className="mb-1 text-xs font-semibold">Ladepunkte:</h4>
                    <div className="flex flex-wrap gap-1">
                      {selectedStation.chargingPoints.map((point, index) => (
                        <Badge
                          key={index}
                          variant="outline"
                          className="flex items-center gap-1 text-xs"
                        >
                          <Battery className="h-3 w-3" />
                          {point.steckertyp} ({point.leistung} kW)
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

              <a
                href={generateAppleMapsUrl(
                  selectedStation.latitude,
                  selectedStation.longitude,
                  selectedStation.name,
                )}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2 flex w-full items-center justify-center gap-1 rounded-md bg-orange-500 px-2 py-1 text-center text-xs text-white no-underline transition-colors hover:bg-orange-600"
              >
                <Navigation className="h-3 w-3" />
                Navigation starten
              </a>
            </div>
          </Popup>
        )}
      </Map>
    </div>
  );
}
